import { useState } from 'react'
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Edit3,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import Button from './Button'
import Input from './Input'

const DEFAULT_REPEATER_FIELD = {
  id: '',
  name: '',
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  description: '',
  options: [],
  allowed_file_types: [],
  max_file_size: null,
  multiple_files: false,
  validation: {
    min: null,
    max: null,
    pattern: null,
  },
}

export default function RepeaterFieldEditor({
  fields = [],
  onChange,
  minItems = 0,
  maxItems = null,
}) {
  const [expandedFields, setExpandedFields] = useState(new Set())
  const [editingField, setEditingField] = useState(null)

  const {
    data: fieldTypesRes,
    isLoading: fieldTypesLoading,
    error: fieldTypesError,
  } = useQuery({
    queryKey: ['field-types'],
    queryFn: () => api.get('/field-types').then((res) => res.data),
    retry: 3,
    staleTime: 5 * 60 * 1000,
  })

  const fieldTypes = fieldTypesRes?.data || []

  const getFieldTypeColor = (fieldType) => {
    const colors = {
      text: 'bg-blue-100 text-blue-800',
      textarea: 'bg-green-100 text-green-800',
      number: 'bg-purple-100 text-purple-800',
      boolean: 'bg-yellow-100 text-yellow-800',
      select: 'bg-indigo-100 text-indigo-800',
      multiselect: 'bg-indigo-100 text-indigo-800',
      file: 'bg-orange-100 text-orange-800',
      url: 'bg-cyan-100 text-cyan-800',
      email: 'bg-pink-100 text-pink-800',
      date: 'bg-emerald-100 text-emerald-800',
      color: 'bg-rose-100 text-rose-800',
      rich_text: 'bg-violet-100 text-violet-800',
      repeater: 'bg-teal-100 text-teal-800',
      entity_selector: 'bg-indigo-200 text-indigo-900',
    }
    return colors[fieldType] || 'bg-gray-100 text-gray-800'
  }

  const addField = () => {
    const newField = {
      ...DEFAULT_REPEATER_FIELD,
      id: `repeater_field_${Date.now()}`,
      name: `field_${fields.length + 1}`,
      label: `Field ${fields.length + 1}`,
    }
    const newFields = [...fields, newField]
    onChange(newFields)
    setEditingField(newField.id)
    setExpandedFields((prev) => new Set([...prev, newField.id]))
  }

  const removeField = (fieldId) => {
    const newFields = fields.filter((field) => field.id !== fieldId)
    onChange(newFields)
    setEditingField(null)
  }

  const updateField = (fieldId, updates) => {
    const newFields = fields.map((field) =>
      field.id === fieldId ? { ...field, ...updates } : field
    )
    onChange(newFields)

    if (updates.type?.toLowerCase() === 'file') {
      setExpandedFields((prev) => new Set([...prev, fieldId]))
    }
  }

  const toggleFieldExpansion = (fieldId) => {
    setExpandedFields((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId)
      } else {
        newSet.add(fieldId)
      }
      return newSet
    })
  }

  const moveField = (fieldId, direction) => {
    const currentIndex = fields.findIndex((field) => field.id === fieldId)
    if (
      (direction === 'up' && currentIndex > 0) ||
      (direction === 'down' && currentIndex < fields.length - 1)
    ) {
      const newFields = [...fields]
      const targetIndex =
        direction === 'up' ? currentIndex - 1 : currentIndex + 1
      ;[newFields[currentIndex], newFields[targetIndex]] = [
        newFields[targetIndex],
        newFields[currentIndex],
      ]
      onChange(newFields)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button
          type="button"
          onClick={addField}
          variant="outline"
          size="sm"
          className="flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Field</span>
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No fields defined yet.</p>
          <p className="text-sm">Click "Add Field" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => {
            const isExpanded = expandedFields.has(field.id)
            const isEditing = editingField === field.id

            return (
              <div
                key={field.id}
                className="border border-gray-200 rounded-lg bg-white"
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        toggleFieldExpansion(field.id)
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getFieldTypeColor(
                          field.type
                        )}`}
                      >
                        {fieldTypes.find((t) => t.name === field.type)?.label ||
                          field.type}
                      </span>
                      <span className="font-medium text-gray-900">
                        {field.label || field.name}
                      </span>
                      {field.required && (
                        <span className="text-red-500 text-xs">*</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          moveField(field.id, 'up')
                        }}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <GripVertical className="h-4 w-4 rotate-90" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          moveField(field.id, 'down')
                        }}
                        disabled={index === fields.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <GripVertical className="h-4 w-4 -rotate-90" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        setEditingField(isEditing ? null : field.id)
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title={isEditing ? 'Cancel editing' : 'Edit field'}
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        removeField(field.id)
                      }}
                      className="p-1 text-red-400 hover:text-red-600"
                      title="Remove field"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4 space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Field Name *
                          </label>
                          <Input
                            value={field.name}
                            onChange={(e) =>
                              updateField(field.id, { name: e.target.value })
                            }
                            placeholder="field_name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Field Label *
                          </label>
                          <Input
                            value={field.label}
                            onChange={(e) =>
                              updateField(field.id, { label: e.target.value })
                            }
                            placeholder="Field Label"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Field Type *
                          </label>
                          <select
                            value={field.type}
                            onChange={(e) =>
                              updateField(field.id, { type: e.target.value })
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            disabled={fieldTypesLoading}
                          >
                            {fieldTypesLoading ? (
                              <option>Loading field types...</option>
                            ) : fieldTypesError ? (
                              <option>Error loading field types</option>
                            ) : (
                              fieldTypes
                                .filter((type) => type.name !== 'repeater')
                                .map((type, typeIndex) => (
                                  <option
                                    key={`${type.name}-${type.id || typeIndex}`}
                                    value={type.name}
                                  >
                                    {type.label}
                                  </option>
                                ))
                            )}
                          </select>
                        </div>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) =>
                                updateField(field.id, {
                                  required: e.target.checked,
                                })
                              }
                              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Required
                            </span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Placeholder
                        </label>
                        <Input
                          value={field.placeholder || ''}
                          onChange={(e) =>
                            updateField(field.id, {
                              placeholder: e.target.value,
                            })
                          }
                          placeholder="Enter placeholder text"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          value={field.description || ''}
                          onChange={(e) =>
                            updateField(field.id, {
                              description: e.target.value,
                            })
                          }
                          rows={2}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          placeholder="Field description"
                        />
                      </div>

                      {(field.type?.toLowerCase() === 'select' ||
                        field.type?.toLowerCase() === 'multiselect') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Options (one per line)
                          </label>
                          <textarea
                            value={field.options?.join('\n') || ''}
                            onChange={(e) => {
                              const options = e.target.value
                                .split('\n')
                                .filter((opt) => opt.trim())
                              updateField(field.id, { options })
                            }}
                            rows={3}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            placeholder="Option 1&#10;Option 2&#10;Option 3"
                          />
                        </div>
                      )}

                      {field.type?.toLowerCase() === 'file' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Allowed File Types
                          </label>
                          <p className="text-sm text-gray-500 mb-2">
                            Select which types of files are allowed for this
                            field
                          </p>
                          <div className="mt-2 space-y-2">
                            {['image', 'video', 'document', 'audio', 'svg'].map(
                              (fileType) => (
                                <label
                                  key={fileType}
                                  className="flex items-center"
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      field.allowed_file_types?.includes(
                                        fileType
                                      ) || false
                                    }
                                    onChange={(e) => {
                                      const currentTypes =
                                        field.allowed_file_types || []
                                      const newTypes = e.target.checked
                                        ? [...currentTypes, fileType]
                                        : currentTypes.filter(
                                            (t) => t !== fileType
                                          )
                                      updateField(field.id, {
                                        allowed_file_types: newTypes,
                                      })
                                    }}
                                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                  />
                                  <span className="ml-2 text-sm text-gray-700 capitalize">
                                    {fileType}s
                                  </span>
                                </label>
                              )
                            )}
                          </div>
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Max File Size (MB)
                            </label>
                            <Input
                              type="number"
                              value={field.max_file_size || ''}
                              onChange={(e) =>
                                updateField(field.id, {
                                  max_file_size: e.target.value
                                    ? parseInt(e.target.value)
                                    : null,
                                })
                              }
                              placeholder="10"
                            />
                          </div>
                          <div className="mt-2">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={field.multiple_files || false}
                                onChange={(e) =>
                                  updateField(field.id, {
                                    multiple_files: e.target.checked,
                                  })
                                }
                                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                Allow multiple file uploads
                              </span>
                            </label>
                          </div>
                        </div>
                      )}

                      {field.type?.toLowerCase() === 'number' && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Min Value
                            </label>
                            <Input
                              type="number"
                              value={field.validation?.min || ''}
                              onChange={(e) =>
                                updateField(field.id, {
                                  validation: {
                                    ...field.validation,
                                    min: e.target.value
                                      ? parseFloat(e.target.value)
                                      : null,
                                  },
                                })
                              }
                              placeholder="Minimum value"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Max Value
                            </label>
                            <Input
                              type="number"
                              value={field.validation?.max || ''}
                              onChange={(e) =>
                                updateField(field.id, {
                                  validation: {
                                    ...field.validation,
                                    max: e.target.value
                                      ? parseFloat(e.target.value)
                                      : null,
                                  },
                                })
                              }
                              placeholder="Maximum value"
                            />
                          </div>
                        </div>
                      )}

                      {(field.type?.toLowerCase() === 'text' ||
                        field.type?.toLowerCase() === 'textarea') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Validation Pattern (Regex)
                          </label>
                          <Input
                            value={field.validation?.pattern || ''}
                            onChange={(e) =>
                              updateField(field.id, {
                                validation: {
                                  ...field.validation,
                                  pattern: e.target.value || null,
                                },
                              })
                            }
                            placeholder="^[a-zA-Z0-9]+$"
                          />
                        </div>
                      )}

                      {field.type?.toLowerCase() === 'entity_selector' && (
                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded">
                          <label className="block text-sm font-semibold text-indigo-900 mb-2">
                            Entity Type *
                          </label>
                          <select
                            value={field.entity_type || 'post'}
                            onChange={(e) =>
                              updateField(field.id, {
                                entity_type: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          >
                            <option value="post">Post</option>
                            <option value="page">Page</option>
                            <option value="category">Category</option>
                            <option value="tag">Tag</option>
                          </select>
                          <label className="flex items-center mt-2">
                            <input
                              type="checkbox"
                              checked={field.multiple_selection ?? true}
                              onChange={(e) =>
                                updateField(field.id, {
                                  multiple_selection: e.target.checked,
                                })
                              }
                              className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-xs text-gray-700">
                              Allow multiple selection
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
